<template>
    <div class="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div class="border-b border-gray-100 px-5 py-4">
            <h2 class="font-semibold text-gray-900">
                {{ t("vue.kiosk_da_detail_records") }}
            </h2>
        </div>

        <!-- Tab bar -->
        <div class="border-b border-gray-100 px-4 py-3">
            <div class="flex flex-wrap gap-2">
                <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    type="button"
                    class="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-all"
                    :class="
                        activeTab === tab.key
                            ? 'bg-indigo-50 text-indigo-900 ring-2 ring-indigo-400'
                            : 'bg-white/60 text-gray-600 ring-1 ring-gray-200 hover:bg-white hover:shadow-sm'
                    "
                    @click="activeTab = tab.key"
                >
                    <div
                        class="flex h-4 w-4 items-center justify-center rounded"
                        :class="tab.bgColor"
                    >
                        <component
                            :is="tab.icon"
                            class="h-2.5 w-2.5 text-white"
                        />
                    </div>
                    <span>{{ tab.label }}</span>
                    <span
                        class="inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold"
                        :class="
                            summary[tab.key] > 0
                                ? tab.badgeClass
                                : 'bg-gray-100 text-gray-400'
                        "
                    >
                        {{ summary[tab.key] }}
                    </span>
                </button>
            </div>
        </div>

        <!-- Active section header -->
        <div
            v-if="activeTabDef"
            class="flex items-center gap-3 border-b border-gray-100 bg-gray-50/60 px-5 py-3"
        >
            <div
                class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg"
                :class="activeTabDef.bgColor"
            >
                <component :is="activeTabDef.icon" class="h-4 w-4 text-white" />
            </div>
            <span class="text-sm font-semibold text-gray-700">
                {{ activeTabDef.label }}
            </span>
            <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                :class="
                    summary[activeTab] > 0
                        ? activeTabDef.badgeClass
                        : 'bg-gray-100 text-gray-400'
                "
            >
                {{ summary[activeTab] }}
            </span>

            <TableSearchInput
                v-if="summary[activeTab] > 0"
                v-model="searchQuery"
            />
        </div>

        <!-- Empty state -->
        <div
            v-if="summary[activeTab] === 0"
            class="px-6 py-16 text-center text-sm text-gray-400"
        >
            {{ t("vue.kiosk_da_no_records") }}
        </div>

        <!-- Users -->
        <UserTable
            v-else-if="
                activeTab === 'new_users' || activeTab === 'verified_emails'
            "
            :items="displayedUserItems"
            :is-verified="activeTab === 'verified_emails'"
        />

        <!-- Yearly subscriptions -->
        <YearlySubscriptionTable
            v-else-if="activeTab === 'yearly_subscriptions'"
            :items="displayedYearlySubs"
        />

        <!-- Activity logs -->
        <ActivityLogTable
            v-else-if="activeTab === 'activity_logs'"
            :items="displayedActivityLogs"
        />

        <!-- Teams -->
        <TeamTable
            v-else
            :items="displayedTeamItems"
            :active-tab-def="activeTabDef"
        />

        <!-- No search results -->
        <div
            v-if="summary[activeTab] > 0 && activeDisplayCount === 0"
            class="px-6 py-12 text-center text-sm text-gray-400"
        >
            {{ t("vue.kiosk_da_no_search_results") }}
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations";
import { useActivityTabs } from "../composables/useActivityTabs";
import { useTableControls } from "../composables/useTableControls";
import TableSearchInput from "./TableSearchInput.vue";
import UserTable from "./tables/UserTable.vue";
import TeamTable from "./tables/TeamTable.vue";
import YearlySubscriptionTable from "./tables/YearlySubscriptionTable.vue";
import ActivityLogTable from "./tables/ActivityLogTable.vue";
import type {
    IActivityLogItem,
    ISummary,
    ITeamItem,
    IUserItem,
    IYearlySubItem,
    TabKey,
} from "../types";

const { t } = useTranslations();

const props = defineProps<{
    summary: ISummary;
    newUsers: IUserItem[];
    verifiedUsers: IUserItem[];
    newTeams: ITeamItem[];
    newFreePlans: ITeamItem[];
    freePlanRestarts: ITeamItem[];
    newSubscriptions: ITeamItem[];
    teamUpdates: ITeamItem[];
    endingTrials: ITeamItem[];
    yearlySubscriptions: IYearlySubItem[];
    activityLogs: IActivityLogItem[];
}>();

const { tabs, activeTab, activeTabDef } = useActivityTabs();
const { searchQuery, applyControls } = useTableControls(activeTab);

const teamDataMap = computed(
    (): Record<TabKey, ITeamItem[]> => ({
        new_users: [],
        verified_emails: [],
        new_teams: props.newTeams,
        new_free_plans: props.newFreePlans,
        free_plan_restarts: props.freePlanRestarts,
        new_subscriptions: props.newSubscriptions,
        team_updates: props.teamUpdates,
        ending_trials: props.endingTrials,
        yearly_subscriptions: [],
        activity_logs: [],
    }),
);

const userDataMap = computed(
    (): Record<TabKey, IUserItem[]> => ({
        new_users: props.newUsers,
        verified_emails: props.verifiedUsers,
        new_teams: [],
        new_free_plans: [],
        free_plan_restarts: [],
        new_subscriptions: [],
        team_updates: [],
        ending_trials: [],
        yearly_subscriptions: [],
        activity_logs: [],
    }),
);

const displayedUserItems = computed((): IUserItem[] =>
    applyControls(userDataMap.value[activeTab.value], [
        "id",
        "name",
        "email",
        "time",
    ]),
);

const displayedTeamItems = computed((): ITeamItem[] =>
    applyControls(teamDataMap.value[activeTab.value], [
        "id",
        "name",
        "owner_email",
        "subscription_level",
        "time",
    ]),
);

const displayedYearlySubs = computed((): IYearlySubItem[] =>
    applyControls(props.yearlySubscriptions, [
        "id",
        "team_name",
        "owner_email",
        "service",
        "plan_type",
        "periodicity",
        "subscription_start_at",
        "subscription_end_at",
    ]),
);

const displayedActivityLogs = computed((): IActivityLogItem[] =>
    applyControls(props.activityLogs, [
        "id",
        "time",
        "team_name",
        "user_email",
        "entity_type",
        "event_code",
        "operation_type",
        "source",
        "message",
    ]),
);

const activeDisplayCount = computed((): number => {
    switch (activeTab.value) {
        case "new_users":
        case "verified_emails":
            return displayedUserItems.value.length;
        case "yearly_subscriptions":
            return displayedYearlySubs.value.length;
        case "activity_logs":
            return displayedActivityLogs.value.length;
        default:
            return displayedTeamItems.value.length;
    }
});
</script>
